import React from "react";
import api from "@/lib/api";
import { X, Gear, Star, WarningCircle } from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";
import SavedQueriesPanel from "@/components/erp/SavedQueriesPanel";
import SuggestionsPanel from "@/components/erp/SuggestionsPanel";
import AgentResultPanel from "@/components/erp/AgentResultPanel";

export default function AgentRunModal() {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents, launchAgent } = useErp();
  const [prompt, setPrompt] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [saved, setSaved] = React.useState([]);
  const [schedules, setSchedules] = React.useState([]);
  const [savedBusy, setSavedBusy] = React.useState(false);
  const [openSchedFor, setOpenSchedFor] = React.useState(null);
  const [schedForm, setSchedForm] = React.useState({ cadence: "monthly", day_of_week: 0, day_of_month: 1, hour_utc: 8 });
  const [fieldValues, setFieldValues] = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [fieldWarnings, setFieldWarnings] = React.useState({});
  const [validating, setValidating] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState([]);
  const [suggesting, setSuggesting] = React.useState(false);

  const agent = agents.find((a) => a.key === openAgent);

  const loadSaved = React.useCallback(async (key) => {
    if (!key) return;
    try {
      const [{ data: sq }, { data: sch }] = await Promise.all([
        api.get(`/erp-agents/saved-queries?agent_key=${key}`),
        api.get(`/erp-agents/schedules`),
      ]);
      setSaved(sq || []);
      setSchedules(sch || []);
    } catch { setSaved([]); setSchedules([]); }
  }, []);

  React.useEffect(() => {
    if (openAgent) loadSaved(openAgent);
  }, [openAgent, loadSaved]);

  // Debounced LLM-backed autosuggest while the user types in the prompt
  React.useEffect(() => {
    if (!openAgent || !prompt.trim() || prompt.trim().length < 8) {
      setSuggestions([]);
      return;
    }
    const id = setTimeout(async () => {
      setSuggesting(true);
      try {
        const { data } = await api.post(`/erp-agents/${openAgent}/suggest`, {
          prompt, current_values: fieldValues,
        });
        const filtered = (data.suggestions || []).filter((s) => !fieldValues[s.field]);
        setSuggestions(filtered);
      } catch { setSuggestions([]); }
      finally { setSuggesting(false); }
    }, 700);
    return () => clearTimeout(id);
  }, [prompt, openAgent]);

  if (!openAgent) return null;

  const close = () => {
    setOpenAgent(null); setPrompt(""); setResult(null);
    setFieldValues({}); setMissingFields([]); setFieldErrors({}); setFieldWarnings({});
  };

  // Convert any rich attachment-object arrays in fieldValues into plain ID lists for the API
  const payloadFieldValues = () => {
    const out = {};
    for (const [k, v] of Object.entries(fieldValues || {})) {
      if (Array.isArray(v) && v.length > 0 && typeof v[0] === "object" && v[0]?.attachment_id) {
        out[k] = v.map((x) => x.attachment_id);
      } else {
        out[k] = v;
      }
    }
    return out;
  };

  const validateOnly = async () => {
    setValidating(true); setFieldErrors({}); setFieldWarnings({});
    try {
      const { data } = await api.post(`/erp-agents/${openAgent}/validate`, {
        prompt, field_values: payloadFieldValues(),
      });
      setFieldErrors(data.field_errors || {});
      setFieldWarnings(data.field_warnings || {});
      return data;
    } catch (e) {
      setResult({ status: "error", message: e?.response?.data?.detail || e.message });
      return { ok: false };
    } finally { setValidating(false); }
  };

  const run = async (forceSubmit = false) => {
    if (!forceSubmit) {
      const v = await validateOnly();
      if (!v?.ok) return;
    } else {
      setFieldErrors({}); setFieldWarnings({});
    }
    setBusy(true); setResult(null); setMissingFields([]);
    try {
      const { data } = await api.post(`/erp-agents/${openAgent}/run`, {
        prompt, field_values: payloadFieldValues(), force_run: forceSubmit,
      });
      setResult(data);
      setMissingFields(data.missing_fields || []);
      if (data.status === "ok") await loadSaved(openAgent);
    } catch (e) {
      setResult({ status: "error", message: e?.response?.data?.detail || "Run failed" });
    } finally { setBusy(false); }
  };

  const onCreateNew = (createAgentKey) => {
    close();
    launchAgent(createAgentKey);
  };

  const saveCurrentRun = async () => {
    if (!result) return;
    const label = window.prompt(
      "Save this query as?", agent?.label ? `${agent.label} — ${new Date().toLocaleDateString()}` : "Saved query",
    );
    if (!label) return;
    setSavedBusy(true);
    try {
      await api.post(`/erp-agents/saved-queries`, {
        agent_key: openAgent, label, prompt, parsed_payload: result.parsed_payload,
        field_values: fieldValues,
      });
      await loadSaved(openAgent);
    } finally { setSavedBusy(false); }
  };

  const replay = async (sid) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/erp-agents/saved-queries/${sid}/replay`);
      setResult(data);
      await loadSaved(openAgent);
    } catch (e) {
      setResult({ status: "error", message: e?.response?.data?.detail || "Replay failed" });
    } finally { setBusy(false); }
  };

  const removeSaved = async (sid) => {
    if (!window.confirm("Delete this saved query?")) return;
    try {
      await api.delete(`/erp-agents/saved-queries/${sid}`);
      await loadSaved(openAgent);
    } catch { /* ignore */ }
  };

  const createSchedule = async (saved_query_id) => {
    try {
      await api.post(`/erp-agents/schedules`, { saved_query_id, ...schedForm });
      setOpenSchedFor(null);
      await loadSaved(openAgent);
    } catch { /* ignore */ }
  };

  const toggleSchedule = async (id) => {
    try {
      await api.put(`/erp-agents/schedules/${id}/toggle`);
      await loadSaved(openAgent);
    } catch { /* ignore */ }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await api.delete(`/erp-agents/schedules/${id}`);
      await loadSaved(openAgent);
    } catch { /* ignore */ }
  };

  const canSave = result?.parsed_payload && result?.status === "ok" && !result?.replayed;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={close}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white text-gray-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-300 shadow-2xl"
        data-testid={`agent-modal-${openAgent}`}>
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion ERP · Vaku AI</div>
            <h3 className="text-2xl font-black tracking-tight mt-1">{agent?.label}</h3>
            <p className="text-xs text-gray-500 mt-1">{agent?.hint}</p>
          </div>
          <button onClick={close} data-testid="agent-modal-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <SavedQueriesPanel
            saved={saved}
            schedules={schedules}
            busy={busy}
            openSchedFor={openSchedFor}
            setOpenSchedFor={setOpenSchedFor}
            schedForm={schedForm}
            setSchedForm={setSchedForm}
            onReplay={replay}
            onRemove={removeSaved}
            onCreateSchedule={createSchedule}
            onToggleSchedule={toggleSchedule}
            onDeleteSchedule={deleteSchedule}
          />

          {/* === Guided wizard (per-agent structured fields) === */}
          <WizardFields
            agentKey={openAgent}
            values={fieldValues}
            onChange={setFieldValues}
            missingFields={missingFields}
            onCreateNew={onCreateNew}
            fieldErrors={fieldErrors}
            fieldWarnings={fieldWarnings}
          />
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase tracking-wider text-gray-600 block">
              Natural-language command <span className="text-gray-400 normal-case font-normal">(optional — the wizard fields above are the source of truth)</span>
            </label>
            <button onClick={() => { close(); setSetupOpen(true); }} data-testid="agent-open-profile"
              className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
              <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
            </button>
          </div>
          <textarea data-testid="agent-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
            className="w-full border border-gray-300 px-3 py-2.5 text-sm font-mono rounded-md focus:outline-none focus:ring-2 focus:ring-[#0020C2]"
            placeholder={agent?.hint} />

          <SuggestionsPanel
            suggesting={suggesting}
            suggestions={suggestions}
            fieldValues={fieldValues}
            setFieldValues={setFieldValues}
            setSuggestions={setSuggestions}
          />

          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => run(false)} disabled={busy || validating} data-testid="agent-run"
              className="rounded-md bg-gradient-to-r from-[#0020C2] to-[#3B5BFF] text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0">
              {validating ? "Validating…" : busy ? "Running…" : "Send to Oracle Fusion"}
            </button>
            <button onClick={validateOnly} disabled={busy || validating} data-testid="agent-validate"
              className="rounded-md border border-[#0020C2] text-[#0020C2] hover:bg-[#0020C2] hover:text-white px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50">
              Dry-run validate
            </button>
            {Object.keys(fieldErrors).length > 0 && (
              <button onClick={() => run(true)} disabled={busy} data-testid="agent-submit-anyway"
                className="rounded-md border border-[#DC2626] text-[#DC2626] hover:bg-red-50 px-3 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
                title="Bypass validation errors and submit anyway">
                Submit anyway
              </button>
            )}
            {canSave && (
              <button onClick={saveCurrentRun} disabled={savedBusy} data-testid="agent-save-query"
                className="rounded-md border border-amber-400 text-amber-700 hover:bg-amber-50 px-3 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
                title="Save this as a saved query for one-click replay later">
                <Star size={14} weight="fill" /> {savedBusy ? "Saving…" : "Save as saved query"}
              </button>
            )}
          </div>

          {/* Summary banner: validation errors (red) or warnings (amber) */}
          {Object.keys(fieldErrors).length > 0 && (
            <div data-testid="validation-errors-banner"
              className="rounded-lg border border-[#DC2626] bg-red-50 px-3 py-2.5 text-xs text-[#DC2626] flex items-start gap-2">
              <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div className="font-bold">{Object.keys(fieldErrors).length} validation error{Object.keys(fieldErrors).length > 1 ? "s" : ""} — fix above or click &quot;Submit anyway&quot;.</div>
            </div>
          )}
          {Object.keys(fieldErrors).length === 0 && Object.keys(fieldWarnings).length > 0 && (
            <div data-testid="validation-warnings-banner"
              className="rounded-lg border border-amber-400 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2">
              <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div className="font-bold">{Object.keys(fieldWarnings).length} warning{Object.keys(fieldWarnings).length > 1 ? "s" : ""} — Oracle could not verify these values, but you can still submit.</div>
            </div>
          )}
          {missingFields.length > 0 && (
            <div data-testid="wizard-missing-banner"
              className="rounded-lg border border-[#DC2626] bg-red-50 px-3 py-2.5 text-xs text-[#DC2626] flex items-start gap-2">
              <WarningCircle size={14} weight="fill" className="mt-0.5 shrink-0" />
              <div>
                <div className="font-bold">Fill the highlighted field{missingFields.length > 1 ? "s" : ""} above to continue.</div>
                <div className="text-[11px] mt-0.5 font-mono">{missingFields.join(" · ")}</div>
              </div>
            </div>
          )}

          <AgentResultPanel result={result} />
        </div>
      </div>
    </div>
  );
}
